import { z, type ZodTypeAny } from 'zod';

import type { SurveyField } from '@/types/provisioning';

/**
 * Extracts enum choices from a survey field title, when present.
 *
 * The backend survey schema only has `text` / `password` types — it has no
 * native enum. Templates encode choices in parenthesised, slash-separated
 * lists, e.g. `Action (start/stop/restart/status)` or
 * `Protocol (amneziawg/wireguard/openvpn)`. We parse that convention so an
 * enum field renders as a <select> instead of a free-text input.
 */
export function parseEnumChoices(field: SurveyField): string[] | undefined {
  if (field.enum && field.enum.length > 0) return field.enum;
  if (field.type !== 'text') return undefined;

  const match = field.title.match(/\(([^)]+)\)/);

  if (!match) return undefined;

  const inner = match[1];

  // Only treat it as an enum when it is a clean slash-separated token list
  // (letters/digits/dashes) — avoids matching parenthetical prose like
  // "(name, IP, or 'all')".
  if (!/^[a-z0-9-]+(\/[a-z0-9-]+)+$/i.test(inner)) return undefined;

  return inner.split('/');
}

/** A survey field enriched with its resolved render kind. */
export interface ResolvedSurveyField extends SurveyField {
  kind: 'text' | 'password' | 'select';
  choices?: string[];
}

/** Resolves each survey field to a concrete input kind for rendering. */
export function resolveSurveyFields(survey: SurveyField[]): ResolvedSurveyField[] {
  return survey.map((field) => {
    if (field.type === 'password') {
      return { ...field, kind: 'password' };
    }

    const choices = parseEnumChoices(field);

    if (choices) {
      return { ...field, kind: 'select', choices };
    }

    return { ...field, kind: 'text' };
  });
}

/**
 * Builds a zod object schema from a template's survey. Required fields must be
 * non-empty; optional fields may be blank. Select fields are constrained to
 * their parsed choice set.
 */
export function buildSurveySchema(fields: ResolvedSurveyField[]): z.ZodObject<Record<string, ZodTypeAny>> {
  const shape: Record<string, ZodTypeAny> = {};

  for (const field of fields) {
    if (field.kind === 'select' && field.choices) {
      const [first, ...rest] = field.choices;
      const enumSchema = z.enum([first, ...rest] as [string, ...string[]]);

      shape[field.name] = field.required ? enumSchema : enumSchema.or(z.literal(''));
      continue;
    }

    const stringSchema = z.string();

    shape[field.name] = field.required
      ? stringSchema.trim().min(1, `${field.title} is required`)
      : stringSchema;
  }

  return z.object(shape);
}

/** Default form values from the survey defaults (blank when absent). */
export function buildSurveyDefaults(fields: ResolvedSurveyField[]): Record<string, string> {
  const defaults: Record<string, string> = {};

  for (const field of fields) {
    defaults[field.name] = field.default ?? '';
  }

  return defaults;
}

/** Drops blank optional fields so they are not sent as empty-string args. */
export function pruneSurveyArgs(
  values: Record<string, string>,
  fields: ResolvedSurveyField[],
): Record<string, unknown> {
  const args: Record<string, unknown> = {};

  for (const field of fields) {
    const value = values[field.name];

    if (value === undefined) continue;
    if (!field.required && value.trim() === '') continue;

    args[field.name] = value;
  }

  return args;
}
